import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Modal, ModalClose, ModalDialog, Stack, Typography } from '@mui/joy';
import { FaPhotoVideo, FaTimes } from 'react-icons/fa';
import { IconWrapper } from '../../components/IconWrapper';
import type { JobCardImage } from './jobCard.types';
import { JOB_CARD_IMAGE_MAX_COUNT, isJobCardVideo } from './jobCardImages';

type PendingImage = {
  key: string;
  file: File;
  previewUrl: string;
};

const thumbSx = {
  width: 88,
  height: 88,
  objectFit: 'cover' as const,
  display: 'block',
  bgcolor: 'neutral.softBg',
};

export function filesFromInput(list: FileList | null): File[] {
  return Array.from(list || []).filter((file) => (
    file.type.startsWith('image/')
    || file.type.startsWith('video/')
    || /\.(jpe?g|png|webp|gif|heic|heif|mp4|mov|webm|m4v|3gp)$/i.test(file.name)
  ));
}

function MediaThumb({
  src,
  name,
  video,
  size = 72,
}: {
  src: string;
  name: string;
  video: boolean;
  size?: number;
}) {
  if (video) {
    return (
      <Box
        component="video"
        src={src}
        muted
        playsInline
        preload="metadata"
        aria-label={name}
        sx={{ ...thumbSx, width: size, height: size }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={name}
      sx={{ ...thumbSx, width: size, height: size }}
    />
  );
}

export function JobCardPendingThumbs({
  files,
  onRemove,
}: {
  files: PendingImage[];
  onRemove: (key: string) => void;
}) {
  if (!files.length) return null;
  return (
    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
      {files.map((item) => (
        <Box key={item.key} sx={{ position: 'relative' }}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'sm', overflow: 'hidden' }}>
            <MediaThumb
              src={item.previewUrl}
              name={item.file.name}
              video={isJobCardVideo(item.file)}
            />
          </Box>
          <IconButton
            size="sm"
            variant="solid"
            color="neutral"
            aria-label={`Remove ${item.file.name}`}
            onClick={() => onRemove(item.key)}
            sx={{ position: 'absolute', top: -8, right: -8, minHeight: 22, minWidth: 22 }}
          >
            <IconWrapper icon={FaTimes} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
}

export const JobCardImageGallery: React.FC<{
  images: JobCardImage[];
  canDelete?: (image: JobCardImage) => boolean;
  deletingId?: string | null;
  onDelete?: (image: JobCardImage) => void;
}> = ({ images, canDelete, deletingId, onDelete }) => {
  const [preview, setPreview] = useState<JobCardImage | null>(null);
  if (!images.length) return null;
  return (
    <>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
        {images.map((image) => {
          const video = isJobCardVideo(image);
          return (
            <Box key={image.id} sx={{ position: 'relative' }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPreview(image)}
                aria-label={image.file_name}
                sx={{
                  p: 0,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'sm',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                }}
              >
                {image.signedUrl ? (
                  <MediaThumb src={image.signedUrl} name={image.file_name} video={video} size={88} />
                ) : (
                  <Box sx={{ width: 88, height: 88, display: 'grid', placeItems: 'center' }}>
                    <Typography level="body-xs">{video ? 'Video' : 'Photo'}</Typography>
                  </Box>
                )}
              </Box>
              {canDelete?.(image) && (
                <IconButton
                  size="sm"
                  variant="solid"
                  color="danger"
                  aria-label={`Delete ${image.file_name}`}
                  disabled={deletingId === image.id}
                  onClick={() => onDelete?.(image)}
                  sx={{ position: 'absolute', top: -8, right: -8, minHeight: 22, minWidth: 22 }}
                >
                  {deletingId === image.id ? <CircularProgress size="sm" /> : <IconWrapper icon={FaTimes} />}
                </IconButton>
              )}
            </Box>
          );
        })}
      </Stack>
      <Modal open={!!preview} onClose={() => setPreview(null)}>
        <ModalDialog sx={{ maxWidth: 'min(920px, 96vw)', p: 1.25 }}>
          <ModalClose />
          {preview?.signedUrl && (
            isJobCardVideo(preview) ? (
              <Box
                component="video"
                src={preview.signedUrl}
                controls
                autoPlay
                playsInline
                sx={{ maxWidth: '100%', maxHeight: '80vh', display: 'block' }}
              />
            ) : (
              <Box
                component="img"
                src={preview.signedUrl}
                alt={preview.file_name}
                sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
              />
            )
          )}
        </ModalDialog>
      </Modal>
    </>
  );
};

export const JobCardAddPhotosButton: React.FC<{
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  onFiles: (files: File[]) => void;
}> = ({ disabled, loading, label = 'Add media', onFiles }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(event) => {
          onFiles(filesFromInput(event.target.files));
          event.target.value = '';
        }}
      />
      <Button
        size="sm"
        variant="outlined"
        disabled={disabled || loading}
        loading={loading}
        startDecorator={<IconWrapper icon={FaPhotoVideo} />}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
    </>
  );
};

export function usePendingImages() {
  const [pending, setPending] = useState<PendingImage[]>([]);

  const addFiles = (files: File[]) => {
    setPending((current) => {
      const room = Math.max(0, JOB_CARD_IMAGE_MAX_COUNT - current.length);
      return [
        ...current,
        ...files.slice(0, room).map((file) => ({
          key: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
        })),
      ];
    });
  };

  const remove = (key: string) => {
    setPending((current) => {
      const next = current.filter((item) => item.key !== key);
      current.filter((item) => item.key === key).forEach((item) => URL.revokeObjectURL(item.previewUrl));
      return next;
    });
  };

  const clear = () => {
    pending.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    setPending([]);
  };

  return { pending, addFiles, remove, clear };
}
